import * as Api from '@ViewModels';
import moment from 'moment';
import React from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import { EventRegistrationFormData, TabValues } from '../utils';

interface UseEventRegistrationFormInitializationProps {
	eventData?: Api.IEventRegistrationSurvey;
	isClone: boolean;
	setValue: UseFormSetValue<EventRegistrationFormData>;
	setState: React.Dispatch<
		React.SetStateAction<{
			enableRequestAttendeesToRSVP: boolean;
			fileInputModalOpen: boolean;
			now: Date;
			selectedImage: Api.IFileAttachment | null;
			customFormIsDisabled: boolean;
			selectedTab: TabValues;
			showEditSurveyName: boolean;
			showReorderModal: boolean;
		}>
	>;
}

// Hook for form initialization and cloning logic
export const useEventRegistrationFormInitialization = ({
	eventData,
	isClone,
	setValue,
	setState,
}: UseEventRegistrationFormInitializationProps) => {
	React.useEffect(() => {
		const sourceData = eventData;
		if (sourceData) {
			const image: Api.IFileAttachment = sourceData.eventInformation?.image || null;
			const rsvpEnabled = sourceData.attendeeOptions?.enabled;
			const startMoment = moment.tz(sourceData.eventInformation?.startTime, sourceData.eventInformation?.timeZone);

			// For clone events, check if the original date is in the past
			if (isClone) {
				// Leave date and time empty for past events when cloning
				setValue('startDate', undefined);
				setValue('startTime', undefined);
			} else {
				// For non-clone (edit mode), set the actual start date and time
				if (sourceData.eventInformation?.startTime) {
					const startMomentLocal = moment.tz(
						sourceData.eventInformation.startTime,
						sourceData.eventInformation?.timeZone
					);
					setValue('startDate', startMomentLocal.format('YYYY/MM/DD'));
					setValue('startTime', startMomentLocal.format('HH:mm:ss'));
				}
			}
			/**
			 * @IF the event has an end time, we will use it
			 * we can check for form value because it was set in the default values or its null
			 */
			if (sourceData?.eventInformation?.endTime) {
				// For clone events with past dates, leave end date and time empty as well
				if (isClone && startMoment.isBefore(moment(), 'day')) {
					setValue('endDate', undefined);
					setValue('endTime', undefined);
				} else if (!isClone) {
					// For non-clone (edit mode), set the actual end date and time
					const endMomentLocal = moment.tz(sourceData.eventInformation.endTime, sourceData.eventInformation?.timeZone);
					setValue('endDate', endMomentLocal.format('YYYY/MM/DD'));
					setValue('endTime', endMomentLocal.format('HH:mm:ss'));
				}
			}

			setValue('guestLimit', sourceData?.attendeeOptions?.guestLimit || 1);
			setValue('details', sourceData?.eventInformation?.details || '');
			setValue('eventName', isClone ? `${sourceData.name} Copy` : sourceData.name || '');
			setValue('location', sourceData?.eventInformation?.location || '');
			setValue('maximumCapacity', sourceData?.attendeeOptions?.maximumCapacity || 1);
			setValue(
				'registrationDeadline',
				sourceData?.attendeeOptions?.registrationDeadline
					? new Date(sourceData.attendeeOptions?.registrationDeadline)
					: null
			);
			setValue('requireDeadline', Boolean(sourceData?.attendeeOptions?.registrationDeadline));
			setValue('requireMaximumCapacity', Boolean(sourceData?.attendeeOptions?.maximumCapacity > 1));
			setValue('setGuestLimit', Boolean(sourceData?.attendeeOptions?.guestLimit));
			setValue('requirePhoneNumber', sourceData?.attendeeOptions?.requirePhoneNumber || false);
			setValue('requireGuestNames', sourceData?.attendeeOptions?.requireGuestNames || false);
			setValue('enableRequestAttendeesToRSVP', sourceData?.attendeeOptions?.enabled || false);
			if (sourceData?.customForm?.fields?.length > 0) {
				if (isClone) {
					// Only do ID regeneration when cloning
					const fieldIdMap = new Map<string, string>();
					setValue(
						'customForm.fields',
						sourceData.customForm.fields.map(field => {
							const newFieldId = uuid();
							fieldIdMap.set(field.id, newFieldId);
							return {
								...field,
								id: newFieldId,
								name: newFieldId, // Generate new name field for response key isolation
								options:
									field.options?.map(option => ({
										...option,
										id: uuid(), // Generate new option ID for clone
										fieldId: newFieldId, // Update option fieldId to reference the new field ID
									})) || [],
							};
						})
					);

					// Update tagGenerationSettings with new field IDs for clones
					if (sourceData?.tagGenerationSettings) {
						setValue(
							'tagGenerationSettings.tagOnCompletion',
							sourceData.tagGenerationSettings.tagOnCompletion || false
						);
						setValue('tagGenerationSettings.completionTag', sourceData.tagGenerationSettings.completionTag || '');
						setValue(
							'tagGenerationSettings.customFormFieldTagSettings',
							sourceData.tagGenerationSettings.customFormFieldTagSettings?.map(tagSetting => ({
								...tagSetting,
								fieldId: fieldIdMap.get(tagSetting.fieldId) || tagSetting.fieldId,
							})) || []
						);
						setValue(
							'tagGenerationSettings.effectiveCompletionTag',
							sourceData.tagGenerationSettings.effectiveCompletionTag || undefined
						);
					}
				} else {
					// When not cloning, use original data as-is
					setValue('customForm.fields', sourceData.customForm.fields);

					// Initialize tagGenerationSettings without field ID mapping
					if (sourceData?.tagGenerationSettings) {
						setValue(
							'tagGenerationSettings.tagOnCompletion',
							sourceData.tagGenerationSettings.tagOnCompletion || false
						);
						setValue('tagGenerationSettings.completionTag', sourceData.tagGenerationSettings.completionTag || '');
						setValue(
							'tagGenerationSettings.customFormFieldTagSettings',
							sourceData.tagGenerationSettings.customFormFieldTagSettings || []
						);
						setValue(
							'tagGenerationSettings.effectiveCompletionTag',
							sourceData.tagGenerationSettings.effectiveCompletionTag || undefined
						);
					}
				}
			} else {
				setValue('customForm.fields', []);

				// Initialize tagGenerationSettings even when no fields exist
				if (sourceData?.tagGenerationSettings) {
					setValue('tagGenerationSettings.tagOnCompletion', sourceData.tagGenerationSettings.tagOnCompletion || false);
					setValue('tagGenerationSettings.completionTag', sourceData.tagGenerationSettings.completionTag || '');
					setValue(
						'tagGenerationSettings.customFormFieldTagSettings',
						sourceData.tagGenerationSettings.customFormFieldTagSettings || []
					);
					setValue(
						'tagGenerationSettings.effectiveCompletionTag',
						sourceData.tagGenerationSettings.effectiveCompletionTag || undefined
					);
				}
			}
			if (sourceData.eventInformation?.timeZone) {
				setValue('timezone', sourceData.eventInformation.timeZone);
			}
			// Set isPaidEvent from backend
			setValue('isPaidEvent', eventData?.isPaidEvent || false);

			// Initialize legacy cost (for backward compatibility)
			setValue(
				'payments.cost',
				eventData?.attendeeOptions?.costPerAttendee ? eventData?.attendeeOptions?.costPerAttendee / 100 : 0
			);

			// Initialize event tickets array
			const hasEventTickets =
				eventData?.attendeeOptions?.eventTickets && eventData.attendeeOptions.eventTickets.length > 0;
			setValue('eventTickets', hasEventTickets ? eventData.attendeeOptions.eventTickets : []);

			setState(
				(prevState: {
					enableRequestAttendeesToRSVP: boolean;
					fileInputModalOpen: boolean;
					now: Date;
					selectedImage: Api.IFileAttachment | null;
					customFormIsDisabled: boolean;
					selectedTab: TabValues;
					showEditSurveyName: boolean;
					showReorderModal: boolean;
				}) => ({
					...prevState,
					enableRequestAttendeesToRSVP: rsvpEnabled,
					selectedImage: image,
					customFormIsDisabled: sourceData?.customFormIsDisabled || false,
				})
			);
			if (eventData.notificationSettings) {
				setValue('notificationSettings.isEnabled', eventData.notificationSettings.isEnabled || false);
				setValue(
					'notificationSettings.additionalRecipients',
					eventData.notificationSettings.additionalRecipients || []
				);
			}
		}
	}, [eventData, setValue, isClone, setState]);
};
